import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  MoreHorizontal,
  Loader,
  AlertTriangle,
  Users,
  Layers,
  LayoutGrid,
} from 'lucide-react';
import { Avatar } from '../components/ui/avatar.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown.tsx';
import { useEnrollments } from '../api/enrollment/enrollmentQueries';
import { useGetDepartments } from '../api/department/departmentQueries';
import { useGetAllSessions } from '../api/session/sessionQueries';
import { useGetAllSubjects } from '../api/subject/subjectQueries';
import { useGetAllClasses } from '../api/class/classQueries';
import { toast } from 'react-toastify';
import { EnrollmentModal } from '../components/sessions/EnrollmentModal';

export default function AcademicDashboard() {
  const navigate = useNavigate();
  const [_, setIsMobile] = useState(false);
  const [isEnrollmentModalOpen, setIsEnrollmentModalOpen] = useState(false);

  const {
    enrollments,
    isLoading: enrollmentsLoading,
    deleteEnrollment,
  } = useEnrollments();
  const { data: departmentsData = [] } = useGetDepartments();
  const { data: sessions = [] } = useGetAllSessions();
  const { data: subjectsData = [] } = useGetAllSubjects();
  const { data: classesData = [], isLoading: classesLoading } =
    useGetAllClasses();

  // Transform departments data to ensure it's an array
  const departments = useMemo(() => {
    if (!departmentsData) return [];
    if (Array.isArray(departmentsData)) return departmentsData;
    if (
      departmentsData.departments &&
      Array.isArray(departmentsData.departments)
    )
      return departmentsData.departments;
    if (departmentsData.data && Array.isArray(departmentsData.data))
      return departmentsData.data;
    return [];
  }, [departmentsData]);

  // Transform sessions data to ensure it's an array
  const sessionsArray = useMemo(() => {
    if (!sessions) return [];
    if (Array.isArray(sessions)) return sessions;
    if (sessions.data && Array.isArray(sessions.data)) return sessions.data;
    return [];
  }, [sessions]);

  // Transform subjects data to ensure it's an array
  const subjects = useMemo(() => {
    if (!subjectsData) return [];
    if (Array.isArray(subjectsData)) return subjectsData;
    if (subjectsData.data && Array.isArray(subjectsData.data))
      return subjectsData.data;
    return [];
  }, [subjectsData]);

  // Transform classes data to ensure it's an array
  const classesArray = useMemo(() => {
    console.log('Raw classes data:', classesData);
    if (!classesData) return [];
    if (Array.isArray(classesData)) return classesData;
    if (classesData.data && Array.isArray(classesData.data))
      return classesData.data;
    return [];
  }, [classesData]);

  // Check screen size on mount and when window resizes
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Filter and sort enrollments
  const filteredStudents = useMemo(() => {
    // Log the raw enrollments data for debugging
    console.log('Raw enrollment data:', enrollments);

    // Log a sample enrollment record if available
    if (enrollments && Array.isArray(enrollments) && enrollments.length > 0) {
      console.log('Sample enrollment record structure:', enrollments[0]);
    } else if (
      enrollments?.data &&
      Array.isArray(enrollments.data) &&
      enrollments.data.length > 0
    ) {
      console.log('Sample enrollment record structure:', enrollments.data[0]);
    }

    // Handle different data structures that might be returned
    let enrollmentsArray = [];
    if (enrollments && Array.isArray(enrollments)) {
      enrollmentsArray = enrollments;
    } else if (enrollments && Array.isArray(enrollments.data)) {
      enrollmentsArray = enrollments.data;
    } else if (enrollments && typeof enrollments === 'object') {
      enrollmentsArray = [enrollments];
    }

    let result = [...enrollmentsArray];

    // Apply search filter if needed
    // ...existing code...

    return result;
  }, [enrollments]);

  const handleDeleteEnrollment = async (enrollmentId) => {
    try {
      await deleteEnrollment(enrollmentId);
      toast.success('Enrollment deleted successfully');
    } catch (error) {
      toast.error('Failed to delete enrollment');
    }
  };

  return (
    <div direction="horizontal" className="min-h-screen">
      <div className="bg-[#f8f9fc] p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div
            className="bg-white p-5 h-32 rounded-xl shadow-sm flex justify-between items-center cursor-pointer"
            onClick={() => navigate('/departments')}
          >
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">
                Departments
              </p>
              <p className="text-2xl font-bold">{departments.length || '0'}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-red-500" />
            </div>
          </div>

          <div
            className="bg-white p-5 h-32 rounded-xl shadow-sm flex justify-between items-center cursor-pointer"
            onClick={() => navigate('/sessions')}
          >
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Sessions</p>
              <p className="text-2xl font-bold">
                {sessionsArray.length || '0'}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <Users className="h-5 w-5 text-green-500" />
            </div>
          </div>

          <div
            className="bg-white p-5 h-32 rounded-xl shadow-sm flex justify-between items-center cursor-pointer"
            onClick={() => navigate('/subjects')}
          >
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">
                Total Subjects
              </p>
              <p className="text-2xl font-bold">{subjects.length || '0'}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
              <Layers className="h-5 w-5 text-pink-500" />
            </div>
          </div>

          <div
            className="bg-white p-5 h-32 rounded-xl shadow-sm flex justify-between items-center cursor-pointer"
            onClick={() => navigate('/classes')}
          >
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Class</p>
              <p className="text-2xl font-bold">
                {classesLoading ? '...' : classesArray.length || '0'}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <LayoutGrid className="h-5 w-5 text-purple-500" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-sm font-medium text-gray-700">
                No of enrolments this session
              </h2>
              <p className="text-2xl font-bold">{filteredStudents.length}</p>
            </div>
            <Button
              className="bg-purple-500 hover:bg-purple-600 text-white rounded-md px-4 py-2 h-auto"
              onClick={() => setIsEnrollmentModalOpen(true)}
            >
              <span className="mr-1">+</span>
              Enroll New Student
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b border-gray-100">
                  <th className="px-4 py-3 font-medium text-sm text-gray-500">
                    Student Name
                  </th>
                  <th className="px-4 py-3 font-medium text-sm text-gray-500">
                    Date
                  </th>
                  <th className="px-4 py-3 font-medium text-sm text-gray-500">
                    Class
                  </th>
                  <th className="px-4 py-3 font-medium text-sm text-gray-500">
                    Session
                  </th>
                  <th className="px-4 py-3 font-medium text-sm text-gray-500">
                    Subject
                  </th>
                  <th className="px-4 py-3 font-medium text-sm text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {enrollmentsLoading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center">
                      <div className="flex items-center justify-center">
                        <Loader className="h-6 w-6 animate-spin mr-2 text-purple-600" />
                        <span>Loading enrollments...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredStudents.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <AlertTriangle className="h-8 w-8 text-orange-500 mb-2" />
                        <span>No enrollments found</span>
                        <Button
                          className="mt-4 bg-purple-500 hover:bg-purple-600"
                          size="sm"
                          onClick={() => setIsEnrollmentModalOpen(true)}
                        >
                          Enroll Student
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredStudents.map((enrollment, index) => {
                    const studentIdDisplay = enrollment.childId
                      ? `Student #${enrollment.childId.slice(-6)}`
                      : 'Unknown';
                    const classIdDisplay = enrollment.classId
                      ? `Class #${enrollment.classId.slice(-6)}`
                      : 'Not Assigned';
                    const sessionIdDisplay = enrollment.sessionId
                      ? `Session #${enrollment.sessionId.slice(-6)}`
                      : 'Not Assigned';

                    const studentName = enrollment.child
                      ? `${enrollment.child.firstName || ''} ${
                          enrollment.child.lastName || ''
                        }`.trim() || enrollment.child.name
                      : enrollment.student
                      ? `${enrollment.student.firstName || ''} ${
                          enrollment.student.lastName || ''
                        }`.trim() || enrollment.student.name
                      : studentIdDisplay;

                    const className =
                      enrollment.class?.className ||
                      enrollment.class?.name ||
                      enrollment.className ||
                      classIdDisplay;

                    const sessionName =
                      enrollment.session?.name ||
                      enrollment.sessionName ||
                      sessionIdDisplay;

                    const subject =
                      enrollment.subject?.subjectName || 'Not Assigned';

                    const formattedDate = enrollment.date
                      ? new Date(enrollment.date).toLocaleDateString()
                      : enrollment.createdAt
                      ? new Date(enrollment.createdAt).toLocaleDateString()
                      : new Date().toLocaleDateString();

                    return (
                      <tr
                        key={enrollment.enrollmentId || enrollment.id || index}
                        className={index % 2 === 0 ? 'bg-[#f8f9fc]' : ''}
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-center">
                            <Avatar className="h-8 w-8 mr-3 rounded-full">
                              <img
                                src="/placeholder.svg?height=32&width=32"
                                alt="Avatar"
                              />
                            </Avatar>
                            <span className="font-medium text-sm text-gray-700">
                              {studentName}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {formattedDate}
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-red-500 font-medium text-sm">
                            {className}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {sessionName}
                        </td>
                        <td className="px-4 py-4">
                          <div className="px-3 py-1 rounded-md text-sm inline-block bg-purple-100 text-purple-600">
                            {subject}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                              <DropdownMenuItem
                                className="text-blue-600"
                                onClick={() =>
                                  navigate(
                                    `/enrollments/${
                                      enrollment.enrollmentId || enrollment.id
                                    }`
                                  )
                                }
                              >
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      'Are you sure you want to delete this enrollment?'
                                    )
                                  ) {
                                    handleDeleteEnrollment(
                                      enrollment.enrollmentId || enrollment.id
                                    );
                                  }
                                }}
                              >
                                Delete Enrollment
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <EnrollmentModal
          open={isEnrollmentModalOpen}
          onOpenChange={setIsEnrollmentModalOpen}
        />
      </div>

      {/* <ResizablePanel defaultSize={25} minSize={20} className="bg-[#f8f9fc]">
        <NotificationsAndEvents />
      </ResizablePanel> */}
    </div>
  );
}
